import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { whatsappAPI, whatsappAnalyticsAPI } from '../lib/api.js';

// Hook for WhatsApp statistics
export const useWhatsAppStats = (params = {}) => {
  return useQuery({
    queryKey: ['whatsapp', 'stats', params],
    queryFn: () => whatsappAPI.getStats(params),
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchInterval: 30 * 1000, // Refetch every 30 seconds for real-time updates
  });
};

// Hook for message history
export const useWhatsAppMessages = (params = {}) => {
  return useQuery({
    queryKey: ['whatsapp', 'messages', params],
    queryFn: () => whatsappAPI.getMessageHistory(params),
    staleTime: 2 * 60 * 1000, // 2 minutes
    refetchInterval: 15 * 1000, // Refetch every 15 seconds for real-time updates
  });
};

// Hook for broadcast campaigns
export const useWhatsAppCampaigns = (params = {}) => {
  return useQuery({
    queryKey: ['whatsapp', 'campaigns', params],
    queryFn: () => whatsappAPI.getBroadcastCampaigns(params),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

// Hook for grievance tickets
export const useWhatsAppGrievances = (params = {}) => {
  return useQuery({
    queryKey: ['whatsapp', 'grievances', params],
    queryFn: () => whatsappAPI.getGrievanceTickets(params),
    staleTime: 2 * 60 * 1000, // 2 minutes
    refetchInterval: 30 * 1000, // Refetch every 30 seconds
  });
};

// Hook for sending messages
export const useSendWhatsAppMessage = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data) => whatsappAPI.sendMessage(data),
    onSuccess: () => {
      // Invalidate and refetch message history and stats
      queryClient.invalidateQueries(['whatsapp', 'messages']);
      queryClient.invalidateQueries(['whatsapp', 'stats']);
    },
  });
};

// Hook for creating broadcast campaigns
export const useCreateBroadcastCampaign = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data) => whatsappAPI.createBroadcastCampaign(data),
    onSuccess: () => {
      // Invalidate and refetch campaigns
      queryClient.invalidateQueries(['whatsapp', 'campaigns']);
    },
  });
};

// Hook for sending broadcast campaigns
export const useSendBroadcastCampaign = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (campaignId) => whatsappAPI.sendBroadcastCampaign(campaignId),
    onSuccess: () => {
      // Invalidate and refetch campaigns and stats
      queryClient.invalidateQueries(['whatsapp', 'campaigns']);
      queryClient.invalidateQueries(['whatsapp', 'stats']);
    },
  });
};

// Hook for getting selectable users
export const useSelectableUsers = (params = {}) => {
  return useQuery({
    queryKey: ['whatsapp', 'selectable-users', params],
    queryFn: () => whatsappAPI.getSelectableUsers(params),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

// Hook for listing APPROVED Meta templates (live fetch)
export const useApprovedTemplates = () => {
  return useQuery({
    queryKey: ['whatsapp', 'approved-templates'],
    queryFn: () => whatsappAPI.getApprovedTemplates(),
    staleTime: 5 * 60 * 1000,
  });
};

// Hook for sending a template broadcast
export const useSendTemplateBroadcast = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data) => whatsappAPI.sendTemplateBroadcast(data),
    onSuccess: () => {
      queryClient.invalidateQueries(['whatsapp', 'campaigns']);
      queryClient.invalidateQueries(['whatsapp', 'stats']);
    },
  });
};

// Hook for broadcast statistics
export const useBroadcastStats = (campaignId, enabled = true) => {
  return useQuery({
    queryKey: ['whatsapp', 'broadcast-stats', campaignId],
    queryFn: () => whatsappAPI.getBroadcastStats(campaignId),
    enabled: enabled && !!campaignId,
    refetchInterval: 10000, // Refetch every 10 seconds for live updates
  });
};

// Hook for updating WhatsApp settings
export const useUpdateWhatsAppSettings = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data) => whatsappAPI.updateSettings(data),
    onSuccess: () => {
      // Invalidate stats to reflect any setting changes
      queryClient.invalidateQueries(['whatsapp', 'stats']);
    },
  });
};

// Hook for WhatsApp analytics counts (optimized)
export const useWhatsAppAnalytics = (filters = {}) => {
  return useQuery({
    queryKey: ['whatsapp-analytics', 'counts', filters],
    queryFn: () => whatsappAnalyticsAPI.getCounts(filters),
    // staleTime: 2 * 60 * 1000, // 2 minutes
    // refetchInterval: 30 * 1000, // Refetch every 30 seconds
  });
};
